import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import routeMeta from './routeMeta.json';
import { isWithHost, WITH_ORIGIN } from './with/config';

export const SITE_URL = 'https://www.apnaaapan.com';
const DEFAULT_IMAGE = `${SITE_URL}/images/og-image.jpg`;
const BRAND = 'Apnaaapan';

// Page titles and descriptions live in routeMeta.json so server.js can put the same tags into the
// HTML it sends (link previews and AI crawlers don't run JavaScript).
// Titles stay under ~60 characters and descriptions under ~160 where possible.
const DEFAULT_META = routeMeta.default;
const ROUTE_META = routeMeta.routes;

function upsertMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

/**
 * Apply title, description, canonical, robots and social tags.
 * Pages that load their own data (case studies, blog posts) call this again once loaded.
 */
export function setPageMeta({ title, description, image, path, canonical, noindex } = {}) {
  const pagePath = path || window.location.pathname;
  const fullTitle = title || DEFAULT_META.title;
  const desc = description || DEFAULT_META.description;
  // With.apnaaapan pages are canonical on their own host.
  const origin = isWithHost() ? WITH_ORIGIN : SITE_URL;
  const url = `${origin}${canonical || pagePath}`;
  const img = image || DEFAULT_IMAGE;

  document.title = fullTitle;
  upsertMeta('name', 'description', desc);
  upsertMeta('name', 'robots', noindex ? 'noindex, nofollow' : 'index, follow');
  upsertCanonical(url);

  upsertMeta('property', 'og:title', fullTitle);
  upsertMeta('property', 'og:description', desc);
  upsertMeta('property', 'og:url', url);
  upsertMeta('property', 'og:image', img);
  upsertMeta('name', 'twitter:title', fullTitle);
  upsertMeta('name', 'twitter:description', desc);
  upsertMeta('name', 'twitter:image', img);
}

export function getRouteMeta(pathname) {
  const clean = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  if (ROUTE_META[clean]) return ROUTE_META[clean];

  if (clean.startsWith('/admin')) {
    return { title: `Admin | ${BRAND}`, description: DEFAULT_META.description, noindex: true };
  }
  if (clean.startsWith('/work-with-us/apply/')) {
    // The apply page sets the role's own title once it has loaded.
    return { title: `Apply | ${BRAND} Careers`, description: ROUTE_META['/work-with-us'].description };
  }
  if (clean.startsWith('/work/')) {
    return { title: `Case Study | ${BRAND}`, description: ROUTE_META['/work'].description };
  }
  if (clean.startsWith('/with/opportunities/')) {
    return { title: 'Opportunity | With.apnaaapan', description: ROUTE_META['/with/opportunities'].description };
  }
  if (clean.startsWith('/with/people/')) {
    return { title: 'Creator | With.apnaaapan', description: ROUTE_META['/with/people'].description };
  }
  if (clean.startsWith('/with/events/')) {
    return { title: 'Event | With.apnaaapan', description: ROUTE_META['/with/community'].description };
  }
  if (clean === '/with' || clean.startsWith('/with/')) {
    return { title: 'Page Not Found | With.apnaaapan', description: ROUTE_META['/with'].description, noindex: true };
  }
  if (clean.startsWith('/blog/')) {
    return { title: `Blog | ${BRAND}`, description: ROUTE_META['/blog'].description };
  }
  if (clean.startsWith('/services/')) {
    return { title: `Services | ${BRAND}`, description: ROUTE_META['/services'].description };
  }
  return { title: `Page Not Found | ${BRAND}`, description: DEFAULT_META.description, noindex: true };
}

/** Sets route-level meta on every navigation. Must run before page-view tracking. */
export function useRouteMeta() {
  const { pathname } = useLocation();
  useEffect(() => {
    const clean = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
    // On with.apnaaapan.com, '/community' uses the meta written for '/with/community'.
    const key = isWithHost() ? (clean === '/' ? '/with' : `/with${clean}`) : pathname;
    setPageMeta({ ...getRouteMeta(key), path: clean });
  }, [pathname]);
}
